import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleMap, Marker, Polyline, useJsApiLoader } from "@react-google-maps/api";
import { ArrowLeft, Navigation } from "lucide-react";
import { io } from "socket.io-client"; 
import { DRIVER_IMAGE_URL, GOOGLE_MAPS_KEY } from "../api/Api"; 

// ─── Distance helper (meters) to optimize the Green Trail ───
const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dp = ((lat2 - lat1) * Math.PI) / 180;
  const dl = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ─── Custom Professional Map Style ───
const mapStyles = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
];

export default function CustomerLiveMap() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const tripDetails = location.state?.tripDetails || null;
  const tripState = location.state?.tripState || "READY_TO_START"; 
  const isTripInProgress = tripState === "IN_PROGRESS";

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_KEY });

  // ── States ──────────────────────────────────────────────────
  const [driverPosition, setDriverPosition] = useState(null);     // 🔴 Driver GPS (Socket)
  const [customerPosition, setCustomerPosition] = useState(null); // 🟢 Customer GPS (Native)
  const [plannedRoute, setPlannedRoute] = useState([]);           // 🔵 Blue Line (Target path)
  const [drivenPath, setDrivenPath] = useState([]);               // 🟢 Green Trail (Actual path)
  
  const [heading, setHeading] = useState(0);
  const [status, setStatus] = useState("Connecting...");
  const [isFollowing, setIsFollowing] = useState(true);

  const mapRef = useRef(null);
  const isFollowingRef = useRef(true);

  const setFollow = (val) => {
    setIsFollowing(val);
    isFollowingRef.current = val;
  };

  // Force reset routes if the trip phase changes (e.g., waiting -> started)
  useEffect(() => {
    setPlannedRoute([]);
    setDrivenPath([]);
  }, [isTripInProgress]);

  // ── Geocode Helper ──────────────────────────────────────────
  const geocode = (address) =>
    new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { address, componentRestrictions: { country: "IN" } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            resolve({ lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() });
          } else {
            reject(status);
          }
        }
      );
    });

  // ============================================================
  // 1. PHASE 1: TRACK DRIVER VIA SOCKET (Waiting for Pickup)
  // ============================================================
  useEffect(() => {
    if (isTripInProgress || !tripDetails?.bookingId) return;

    setStatus("Locating Driver...");
    const socket = io(DRIVER_IMAGE_URL, { transports: ["websocket"] });

    socket.on("connect", () => {
      console.log(`✅ [SOCKET CONNECTED] Customer app joined room: booking_${tripDetails.bookingId}`);
      socket.emit("join_booking_room", tripDetails.bookingId);
    });

    socket.on("driver_location_update", (data) => {
      // 🟢 DEBUG LOG: Verify driver coordinates are arriving
      console.log("📍 [LIVE DRIVER GPS RECEIVED]:", data);

      const newPos = { lat: Number(data.lat), lng: Number(data.lng) };
      setDriverPosition(newPos);
      if (data.heading) setHeading(Number(data.heading));
      setStatus("Driver Approaching 🟢");

      if (mapRef.current && isFollowingRef.current) {
        mapRef.current.panTo(newPos);
      }
    });

    return () => {
      console.log("🛑 [SOCKET DISCONNECTED] Customer left driver tracking.");
      socket.disconnect();
    };
  }, [isTripInProgress, tripDetails]);

  // ============================================================
  // 2. PHASE 2: TRACK CUSTOMER VIA NATIVE GPS (Trip in Progress)
  // ============================================================
  useEffect(() => {
    if (!isTripInProgress || !navigator.geolocation) return;

    setStatus("Acquiring GPS location...");
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, heading: newHeading } = position.coords;
        const newPos = { lat: latitude, lng: longitude };
        
        // 🟢 DEBUG LOG: Verify customer's own phone GPS is tracking properly
        console.log("🚶‍♂️ [NATIVE CUSTOMER GPS]:", newPos);

        setCustomerPosition(newPos);
        if (newHeading) setHeading(newHeading);
        setStatus("Live Tracking Active 🟢");

        setDrivenPath((prev) => {
          if (!prev.length) return [newPos];
          const lastPoint = prev[prev.length - 1];
          const dist = getDistance(lastPoint.lat, lastPoint.lng, latitude, longitude);
          return dist > 5 ? [...prev, newPos] : prev;
        });

        if (mapRef.current && isFollowingRef.current) {
          mapRef.current.panTo(newPos);
        }
      },
      (error) => {
        console.error("GPS Error:", error);
        setStatus("GPS Error. Enable permissions.");
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [isTripInProgress]);

  // ============================================================
  // 3. DRAW DYNAMIC BLUE ROUTE
  // ============================================================
  useEffect(() => {
    if (!isLoaded || !tripDetails || plannedRoute.length > 0) return;

    const drawRoute = async () => {
      try {
        let originPos;
        let targetAddress;

        // Determine origin and target based on Phase
        if (isTripInProgress) {
          if (!customerPosition) return; // Wait for GPS
          originPos = customerPosition;
          targetAddress = tripDetails.destinations;
        } else {
          if (!driverPosition) return; // Wait for Socket GPS
          originPos = driverPosition;
          targetAddress = tripDetails.pickup;
        }

        if (!targetAddress || targetAddress === "Not Specified") return;

        const targetPos = await geocode(targetAddress);
        const directionsService = new window.google.maps.DirectionsService();

        directionsService.route(
          {
            origin: originPos,
            destination: targetPos,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, routeStatus) => {
            if (routeStatus === "OK") {
              const points = result.routes[0].overview_path.map((p) => ({
                lat: p.lat(),
                lng: p.lng(),
              }));
              setPlannedRoute(points);
            }
          }
        );
      } catch (err) {
        console.error("Route Error:", err);
      }
    };

    drawRoute();
  }, [isLoaded, tripDetails, customerPosition, driverPosition, isTripInProgress, plannedRoute]);


  // Active center coordinates based on phase
  const activeCenter = isTripInProgress ? customerPosition : driverPosition;

  if (!isLoaded) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center bg-light min-vh-100">
        <div className="spinner-border text-primary mb-2" />
        <div className="fw-bold">Loading Live Map...</div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column bg-light" style={{ height: "100vh", overflow: "hidden" }}>
      
      {/* ── Top Bar ── */}
      <div className="bg-white shadow-sm px-3 py-3 d-flex align-items-center gap-3" style={{ zIndex: 10 }}>
        <button className="btn btn-light rounded-circle p-2" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <div className="flex-grow-1">
          <h5 className="mb-0 fw-bold text-dark">
            {isTripInProgress ? "Trip to Destination" : "Driver Arriving"}
          </h5>
          <small className="text-success fw-semibold">{status}</small>
        </div>
      </div>

      {/* ── Main Map Area ── */}
      <div className="position-relative flex-grow-1">
        
        {/* Recenter Button */}
        {!isFollowing && activeCenter && (
          <button
            onClick={() => {
              setFollow(true);
              mapRef.current.panTo(activeCenter);
              mapRef.current.setZoom(16);
            }}
            className="btn btn-primary shadow-lg position-absolute fw-bold text-white d-flex align-items-center"
            style={{ top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 5, borderRadius: 30, padding: "10px 20px" }}
          >
            <Navigation size={16} className="me-2" /> 
            {isTripInProgress ? "Center on Me" : "Find Driver"}
          </button>
        )}

        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={activeCenter || { lat: 20.2961, lng: 85.8245 }} 
          zoom={16}
          onLoad={(map) => (mapRef.current = map)}
          onDragStart={() => setFollow(false)}
          options={{
            zoomControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            gestureHandling: "greedy",
            styles: mapStyles,
          }}
        >
          {/* 🔵 Blue Planned Route */}
          {plannedRoute.length > 1 && (
            <Polyline
              path={plannedRoute}
              options={{ strokeColor: "#0d6efd", strokeOpacity: 0.6, strokeWeight: 5 }}
            />
          )}

          {/* 🟢 Green Driven Trail (Only shows during In-Progress) */}
          {isTripInProgress && drivenPath.length > 1 && (
            <Polyline
              path={drivenPath}
              options={{ strokeColor: "#198754", strokeOpacity: 1, strokeWeight: 5 }}
            />
          )}

          {/* 🚗 LIVE MARKER (Switches between Driver & Customer based on phase) */}
          {activeCenter && (
            <Marker
              position={activeCenter}
              title={isTripInProgress ? "You are here" : "Driver"}
              icon={
                window.google && window.google.maps
                  ? {
                      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                      scale: 6,
                      fillColor: isTripInProgress ? "#0d6efd" : "#198754", // Blue for Customer, Green for Driver
                      fillOpacity: 1,
                      strokeColor: "#ffffff",
                      strokeWeight: 2,
                      rotation: heading,
                      anchor: new window.google.maps.Point(0, 2.5),
                    }
                  : undefined
              }
            />
          )}

          {/* 📍 Target Marker */}
          {plannedRoute.length > 0 && (
            <Marker
              position={plannedRoute[plannedRoute.length - 1]}
              title="Destination"
              icon={{ url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png" }} 
            />
          )}
        </GoogleMap>
      </div>

      {/* ── Info Footer & Map Legend ── */}
      <div className="bg-white p-3 shadow-sm rounded-top-4 position-relative d-flex flex-column gap-3" style={{ zIndex: 10, marginTop: "-15px" }}>
        
        {/* Destination Info */}
        {tripDetails && (
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h6 className="fw-bold m-0 text-dark">
                {isTripInProgress ? "Drop-off Target" : "Pickup Target"}
              </h6>
              <span className="text-muted small text-truncate d-block" style={{ maxWidth: "220px" }}>
                {isTripInProgress ? tripDetails.destinations : tripDetails.pickup}
              </span>
            </div>
            <div className="text-end">
              <span className={`badge px-3 py-2 rounded-pill fs-6 fw-bold ${isTripInProgress ? "bg-success bg-opacity-10 text-success" : "bg-warning bg-opacity-10 text-warning"}`}>
                {isTripInProgress ? "En Route" : "Arriving"}
              </span>
            </div>
          </div>
        )}

        {/* Minimal Map Legend */}
        <div className="d-flex justify-content-around border-top pt-3 mt-1">
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: "20px", height: "4px", backgroundColor: "#0d6efd", borderRadius: "2px", opacity: 0.6 }}></div>
            <span className="text-muted fw-bold" style={{ fontSize: "10px", textTransform: "uppercase" }}>Planned Route</span>
          </div>
          {isTripInProgress && (
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: "20px", height: "4px", backgroundColor: "#198754", borderRadius: "2px" }}></div>
              <span className="text-muted fw-bold" style={{ fontSize: "10px", textTransform: "uppercase" }}>Actual Path</span>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}